import {
  CommentModal,
  DispatchRequestDataGrid,
  DispatchRequestDispatchAction,
  ResizeableContent,
  ThemedPage,
  useDispatchRequestMutation
} from "@ots-frontend/ots-ui";
import {Card, Container, Group, LoadingOverlay, Menu, rem, Stack, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useState} from "react";
import {DispatchRequestEntity} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {useDisclosure} from "@mantine/hooks";
import {useGetPendingDispatchRequests} from "../../hooks/useGetPendingDispatchRequests";

export type DispatchRequestPendingPageProps = {}

export const DispatchRequestPendingPage = (props: DispatchRequestPendingPageProps) => {

  const [selectedDispatchRequest, setSelectedDispatchRequest] = useState<DispatchRequestEntity | undefined>()

  const navigate = useNavigate();
  const {t} = useTranslation(["translation", "common"]);
  const rejectDisclosure = useDisclosure(false)

  const {
    dispatchRequests,
    isFetching,
    isLoading,
    refetch
  } = useGetPendingDispatchRequests();

  const {
    rejectDispatchRequest,
    isRejecting
  } = useDispatchRequestMutation()

  const onActionClick = useCallback((data: DispatchRequestEntity, action: DispatchRequestDispatchAction) => {
    if (action === 'ACCEPT') {
      navigate(`/requests/accept`, {state: {id: data.id}})
    } else if (action === 'REJECT') {
      rejectDisclosure[1].open()
    }
    setSelectedDispatchRequest(data)
  }, [rejectDisclosure, navigate])

  const onRejectSubmit = useCallback((comments: string | undefined) => {
    if (selectedDispatchRequest && comments) {
      rejectDispatchRequest(selectedDispatchRequest.id, comments, undefined)
      rejectDisclosure[1].close()
    }
  }, [rejectDispatchRequest, selectedDispatchRequest, rejectDisclosure])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Title
                      order={4}>{t("page.dispatchrequestspending.title", "Pending Dispatch Requests", {ns: "translation"})}</Title>
                    <Menu
                      width={260}
                      position="bottom-end"
                      transitionProps={{transition: "pop-top-right"}}
                      withinPortal
                    >
                      <Menu.Target>
                        <UnstyledButton>
                          <Group spacing={7}>
                            <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                              {t('common.action.plural', "Actions")}
                            </Text>
                            <IconChevronDown size={rem(12)} stroke={1.5}/>
                          </Group>
                        </UnstyledButton>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item onClick={() => refetch()}>
                          {t("actions.refresh", "Refresh", {ns: "common"})}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Card>
                <LoadingOverlay visible={isFetching} overlayBlur={2}/>
                <DispatchRequestDataGrid
                  data={dispatchRequests}
                  onActionClick={onActionClick}/>
              </Stack>

              <CommentModal
                title={t<string>("profile.dispatchrequest.action.reject.alert.title", "Reject")}
                opened={rejectDisclosure[0]}
                onClose={rejectDisclosure[1].close}
                onSubmit={onRejectSubmit}
                loading={isRejecting}
                color="red"
                message={
                  <Text size="sm">
                    {t("profile.dispatchrequest.action.reject.alert.message", "Are you sure you want to reject this dispatch request?")}
                  </Text>
                }/>

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
